/**
 * commitmentFaq — the plain-language answers, in one place.
 *
 * WHY THIS EXISTS. Five excellent answers to what a buyer is actually
 * committing to lived inside `src/pages/Subscription.tsx`, which sits behind
 * ProtectedRoute — so the only people who could read them were people who had
 * ALREADY paid. The person who needs "can our bill surprise us?" answered is
 * the one still deciding, and unpredictability is the known objection to
 * usage-based pricing. Two copies would drift, so both pages render this one.
 *
 * The four LAW-FIRM questions after the commitment five are new, and each is a
 * question a firm asks during procurement rather than one a vendor enjoys being
 * asked. Every answer states what the code does. Nothing here describes a
 * control Prest0 does not have — `scripts/check-security-claims.mjs` is the
 * site-wide backstop — and two omissions are deliberate:
 *
 *   - NO retention-window promise. "Configurable 30/60/90-day timers with
 *     deletion receipts" was removed site-wide in the security sweep because no
 *     such surface exists. "Deleted on request" is what is true.
 *   - NO compliance certification of our own, and no legal advice about who is
 *     responsible. The answer describes the supervision the product enforces
 *     and leaves the professional obligation to the firm's own counsel.
 *
 * ONE ANSWER PER LINE, deliberately. `scripts/generate-guest-knowledge-source`
 * scrapes these into the signed-out Prest0n's grounding, and its copy-property
 * matcher takes the FIRST quoted run after `answer:` — so a `'…' + '…'`
 * continuation chain silently loses everything after the first fragment. The
 * first draft of this file was written that way and cost "deleted on request"
 * and "renewal date" their place in what the model knows. Long lines are the
 * price of the whole sentence surviving; there is no max-len rule here, and
 * `src/config` already carries 300-character lines.
 *
 * The one interpolated answer keeps the allocation in a trailing fragment so
 * the bulk of the sentence still scrapes as one literal.
 */

import { MONTHLY_WU_PER_PRESTON, WU_LABEL, WU_PRICING } from './workUnits';

export interface FaqEntry {
  id: string;
  question: string;
  answer: string;
}

const ALLOCATION = `${MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')} ${WU_LABEL}`;

/**
 * What a committed term means. Wording verbatim from the subscription page,
 * where these were written and owner-reviewed (the "open by default" directive
 * of 2026-08-15 governs how they render, not what they say).
 */
export const COMMITMENT_FAQ: readonly FaqEntry[] = Object.freeze([
  {
    id: 'what-committing-to',
    question: 'what exactly am I committing to?',
    answer:
      `a number of Prest0ns for your term — a team size, not a usage bill. annual means those Prest0ns, at that rate, for twelve months. each month's allocation is good for that month and the next, then expires — like payroll, you staff for the workload you have. each Prest0n brings ${ALLOCATION} a month, yours at any pace within that window.`,
  },
  {
    id: 'add-mid-term',
    question: 'can we add Prest0ns mid-term?',
    answer:
      "anytime, in one click. new hires are prorated and end together with your existing term — and every Prest0n's rate falls automatically as the team grows.",
  },
  {
    id: 'reduce',
    question: 'can we reduce the number of Prest0ns?',
    answer:
      'on a committed term — annual or the prepaid year — from the next term: one click, anytime before renewal. the commitment is what the discount pays for. on a monthly term, anytime — changes take effect at your next monthly cycle.',
  },
  {
    id: 'renewal',
    question: 'what happens at renewal?',
    answer:
      "your subscription renews automatically at your same team size unless you change it — one click, anytime before your renewal date. no notice periods, no phone calls. your rate is locked for the length of each term you're in — it never changes mid-term.",
  },
  {
    id: 'bill-surprise',
    question: 'can our bill surprise us?',
    answer:
      'no. your committed team is a fixed number, and anything beyond it — overtime, top-ups, sprint blocks — exists only when an administrator authorizes it in advance. there is no such thing as an automatic overage charge at prest0.',
  },
]);

/**
 * The four a law firm asks during procurement. Public-page only: a paying
 * customer has already had these answered by a contract.
 */
export const FIRM_FAQ: readonly FaqEntry[] = Object.freeze([
  {
    id: 'training',
    question: 'is our client information used to train AI models?',
    answer:
      'no. your work is never used to train a model — not ours, and not a provider’s. that is a commitment we hold contractually with the model providers, not a setting you have to find and switch off.',
  },
  {
    id: 'supervision',
    question: 'who is responsible for what Prest0n does?',
    answer:
      'you are, and the product is built to keep it that way. anything that leaves your firm — an email, a message to a client — waits for the person accountable for it to authorize it, on the act itself. reading and preparing are free; sending is a decision a person makes. every governed action lands in an append-only audit feed your team can read, so there is a record of who asked for what.',
  },
  {
    id: 'roles',
    question: 'what can each person on my team do?',
    answer:
      'exactly what their role includes, and nothing else. five roles out of the box — owner, admin, member, assistant and read-only — and you can build your own from individual permissions. access is resolved for the person asking, on every single message, from your live team record: a role that does not include something is refused, not filtered afterwards. you can try that on the home page before you sign up.',
  },
  {
    id: 'leaving',
    question: 'what happens to our data if we leave?',
    answer:
      'it is yours. deliverables are files you already have, and we delete your workspace data on request. there is no export fee and no notice period — your subscription stops at the end of the term you are in.',
  },
]);

/** Everything the public pricing page shows, in reading order. */
export const PRICING_PAGE_FAQ: readonly FaqEntry[] = Object.freeze([
  ...COMMITMENT_FAQ,
  ...FIRM_FAQ,
]);

/** The heading above the commitment set, shared by both pages. */
export const COMMITMENT_FAQ_HEADING = 'what your commitment means — in plain language';

/**
 * Re-exported so a consumer rendering the ladder beside these answers reads the
 * same constant they were written against rather than importing workUnits
 * separately and drifting.
 */
export const PREPAY_DISCOUNT_PCT = WU_PRICING.prepayDiscountPct;
